import { AlertTriangle } from 'lucide-react';
import { cn } from '@rudi/ui/lib/utils';

const LEVELS = {
  normal: { track: 'bg-primary/15', fill: 'bg-primary', label: null },
  high: { track: 'bg-amber-500/20', fill: 'bg-amber-500', label: 'High' },
  critical: { track: 'bg-destructive/20', fill: 'bg-destructive', label: 'Critical' },
} as const;

export function meterLevel(ratio: number): keyof typeof LEVELS {
  if (ratio >= 0.9) return 'critical';
  if (ratio >= 0.8) return 'high';
  return 'normal';
}

/**
 * One value against its limit. The fill turns amber at 80% and red at 90%,
 * and the text names the level, so the state never depends on color alone.
 */
export function Meter({
  label,
  used,
  total,
  format,
  detail,
  summary,
}: {
  label: string;
  used: number | null;
  total: number;
  format: (value: number) => string;
  detail?: string;
  /** Replaces "<used> of <total> · <percent>", for figures that are already percentages. */
  summary?: string;
}) {
  const ratio = used !== null && total > 0 ? used / total : null;
  const level = LEVELS[meterLevel(ratio ?? 0)];
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-2 text-sm">
        <span className="font-medium">{label}</span>
        <span className="text-muted-foreground tabular-nums">
          {ratio === null ? (
            'No data'
          ) : summary ? (
            summary
          ) : (
            <>
              {format(used!)} of {format(total)} · {Math.round(ratio * 100)}%
            </>
          )}
        </span>
      </div>
      <div
        role="meter"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={used ?? undefined}
        aria-valuetext={ratio === null ? 'No data' : `${Math.round(ratio * 100)}%`}
        className={cn(
          'h-2 overflow-hidden rounded-full',
          ratio === null ? 'bg-muted' : level.track
        )}
      >
        {ratio !== null && (
          <div
            className={cn('h-full rounded-full', level.fill)}
            style={{ width: `${Math.min(100, ratio * 100)}%` }}
          />
        )}
      </div>
      {(detail || (ratio !== null && level.label)) && (
        <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>{detail}</span>
          {ratio !== null && level.label && (
            <span className="inline-flex items-center gap-1 font-medium text-foreground">
              <AlertTriangle className="h-3 w-3" aria-hidden />
              {level.label}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
